import { useEffect, useState, useCallback, useRef } from 'react';
import {
  getQueueCount,
  getFailedEvents,
  syncQueue,
  retryFailedEvents,
  discardFailedEvents,
  OFFLINE_QUEUE_CHANGED,
  type QueuedEvent,
} from './queue';
import { apiRequest } from '@/api/client';
import { buildReplayRequest, compactBody } from '@/lib/offlineReplay';
import { useOnlineStatus } from '@/hooks';
import { useConfirm } from '@/components/Feedback';
import { CrewIcon } from '@/components/CrewUi';

/** Process-wide single-flight so dual useOfflineSync mounts cannot double-POST. */
let flushInFlight: Promise<void> | null = null;

/** Replays one saved action. The same Idempotency-Key / clientEventId / device time are sent
 * on every attempt, so a replay of a request that already reached the server returns the
 * original result instead of being applied twice. */
async function processEvent(event: QueuedEvent): Promise<void> {
  const request = buildReplayRequest(event);
  if (!request) return;
  await apiRequest(request.path, {
    method: 'POST',
    body: compactBody(request.body),
    headers: { 'Idempotency-Key': request.idempotencyKey },
  });
}

export function useOfflineSync() {
  const online = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [failedEvents, setFailedEvents] = useState<QueuedEvent[]>([]);
  const [syncing, setSyncing] = useState(false);
  const wasOnline = useRef(online);

  const refreshCount = useCallback(async () => {
    try {
      const [count, failed] = await Promise.all([getQueueCount(), getFailedEvents()]);
      setPendingCount(count);
      setFailedEvents(failed);
      return count;
    } catch {
      return 0;
    }
  }, []);

  const flushQueue = useCallback(async () => {
    if (flushInFlight) {
      await flushInFlight;
      await refreshCount();
      return;
    }
    setSyncing(true);
    flushInFlight = (async () => {
      try {
        const count = await getQueueCount();
        setPendingCount(count);
        if (count > 0) {
          await syncQueue(processEvent);
          await refreshCount();
        }
      } finally {
        flushInFlight = null;
        setSyncing(false);
      }
    })();
    await flushInFlight;
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  // Refresh pending count immediately after queueEvent writes to IndexedDB.
  useEffect(() => {
    const onQueueChanged = () => {
      void refreshCount();
    };
    window.addEventListener(OFFLINE_QUEUE_CHANGED, onQueueChanged);
    return () => window.removeEventListener(OFFLINE_QUEUE_CHANGED, onQueueChanged);
  }, [refreshCount]);

  // On reconnect (or when already online with pending work), read IDB directly
  // so we don't depend on a stale pendingCount from before queueEvent ran.
  useEffect(() => {
    const cameOnline = online && !wasOnline.current;
    wasOnline.current = online;
    if (online && (cameOnline || pendingCount > 0)) {
      void flushQueue();
    }
  }, [online, pendingCount, flushQueue]);

  // Also listen to the browser online event for an immediate flush attempt.
  useEffect(() => {
    const onOnline = () => {
      void flushQueue();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flushQueue]);

  const retryFailed = useCallback(async () => {
    await retryFailedEvents();
    await refreshCount();
    if (typeof navigator === 'undefined' || navigator.onLine) await flushQueue();
  }, [refreshCount, flushQueue]);

  const discardFailed = useCallback(async () => {
    await discardFailedEvents();
    await refreshCount();
  }, [refreshCount]);

  return {
    online,
    pendingCount,
    failedEvents,
    syncing,
    refreshCount,
    flushQueue,
    retryFailed,
    discardFailed,
  };
}

const EVENT_LABELS: Record<QueuedEvent['type'], string> = {
  scan: 'Scan',
  cost: 'Cost',
  stop_advance: 'Stop update',
  crew_event: 'Trip update',
  pod: 'Signature',
  exception: 'Exception',
  scan_correction: 'Scan fix',
  cargo_check: 'Cargo check',
};

function itemsLabel(n: number): string {
  return `${n} item${n === 1 ? '' : 's'}`;
}

export function OfflineIndicator() {
  const { online, pendingCount, failedEvents, syncing, retryFailed, discardFailed } = useOfflineSync();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);

  const failedCount = failedEvents.length;
  if (online && pendingCount === 0 && !syncing && failedCount === 0) return null;

  let pendingText: string | null = null;
  if (!online) {
    pendingText =
      pendingCount === 0
        ? 'No signal – you can keep working'
        : `No signal – ${itemsLabel(pendingCount)} saved on phone, will send automatically`;
  } else if (pendingCount > 0 || syncing) {
    pendingText = pendingCount > 0 ? `Sending ${itemsLabel(pendingCount)} saved on phone…` : 'Sending saved items…';
  }

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {pendingText && (
        <div className={`offline-bar ${online ? 'offline-bar-sending' : 'offline-bar-nosignal'}`} role="status" aria-live="polite">
          <CrewIcon name={online ? 'sync' : 'offline'} className="offline-icon" />
          <span>{pendingText}</span>
        </div>
      )}
      {failedCount > 0 && (
        <div className="offline-failed" role="alert">
          <strong className="offline-failed-title">
            <CrewIcon name="error" className="offline-icon" />
            {itemsLabel(failedCount)} couldn&apos;t send
          </strong>
          <ul>
            {failedEvents.slice(0, 3).map((e) => (
              <li key={e.id}>
                {EVENT_LABELS[e.type] ?? 'Item'}: {e.lastError ?? 'Rejected by the server'}
              </li>
            ))}
            {failedCount > 3 && <li>…and {failedCount - 3} more</li>}
          </ul>
          <p className="offline-failed-hint">If Retry keeps failing, call dispatch before discarding.</p>
          <div className="offline-failed-actions">
            <button type="button" disabled={busy} onClick={() => void run(retryFailed)}>
              Retry
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                const ok = await confirm({
                  title: `Discard ${itemsLabel(failedCount)}?`,
                  message: `${itemsLabel(failedCount)} will be deleted from this phone and never sent. This can't be undone — call dispatch first if you're not sure.`,
                  confirmLabel: 'Discard',
                  cancelLabel: 'Keep',
                  danger: true,
                });
                if (ok) void run(discardFailed);
              }}
            >
              Discard
            </button>
          </div>
        </div>
      )}
      <style>{`
        .offline-failed {
          background: #7a2a1f;
          color: #fff;
          padding: var(--space-sm) var(--space-md);
          font-size: 0.875rem;
        }
        .offline-failed ul { margin: var(--space-xs) 0; padding-left: 1.25rem; }
        .offline-failed-actions { display: flex; gap: var(--space-sm); margin-top: var(--space-xs); }
        .offline-failed-actions button {
          min-height: 44px;
          min-width: 96px;
          padding: 0 var(--space-md);
          border-radius: var(--radius-md);
          border: 1px solid rgba(255,255,255,0.6);
          background: rgba(255,255,255,0.12);
          color: #fff;
          font-weight: 600;
          font-size: 0.9375rem;
        }
        .offline-failed-actions button:first-child { background: #fff; color: #7a2a1f; }
        .offline-failed-hint { margin: var(--space-xs) 0 0; opacity: 0.85; font-size: 0.8125rem; }
        .offline-bar { display: flex; align-items: center; justify-content: center; gap: var(--space-sm); }
        .offline-icon { width: 20px; height: 20px; flex-shrink: 0; }
        .offline-failed-title { display: flex; align-items: center; gap: var(--space-sm); }
        /* Crew shell: Android-style banners (amber no-signal, neutral sending, red failed). */
        .crew-shell .offline-bar {
          justify-content: flex-start;
          text-align: left;
          gap: var(--cs-3);
          padding: var(--cs-3) var(--crew-pad);
          font-size: var(--crew-fs-body);
          font-weight: 600;
          line-height: 1.35;
        }
        .crew-shell .offline-bar-nosignal { background: var(--crew-amber); color: var(--crew-bg); }
        .crew-shell .offline-bar-sending { background: var(--crew-high); color: var(--crew-white); }
        .crew-shell .offline-icon { width: 26px; height: 26px; }
        .crew-shell .offline-failed {
          background: var(--crew-urgent);
          padding: var(--cs-4) var(--crew-pad);
          font-size: var(--crew-fs-body);
        }
        .crew-shell .offline-failed-title { font-size: 1.1875rem; gap: var(--cs-3); }
        .crew-shell .offline-failed ul { list-style: none; padding: 0; margin: var(--cs-2) 0; }
        .crew-shell .offline-failed li { overflow-wrap: anywhere; }
        .crew-shell .offline-failed-hint { opacity: 1; font-size: var(--crew-fs-support); margin: 0 0 var(--cs-3); }
        .crew-shell .offline-failed-actions { display: grid; grid-template-columns: 1fr 1fr; gap: var(--cs-3); margin: 0; }
        .crew-shell .offline-failed-actions button {
          min-height: var(--crew-btn);
          min-width: 0;
          border-radius: var(--crew-r);
          font-size: var(--crew-fs-button);
          font-weight: 700;
          border: 2px solid rgba(255,255,255,0.85);
          background: transparent;
          color: #fff;
        }
        .crew-shell .offline-failed-actions button:first-child { background: #fff; color: var(--crew-bg); border-color: #fff; }
      `}</style>
    </>
  );
}
